import { Dto, IDName } from '@damofire/data-commons';
import { Timestamp } from 'firebase/firestore';

export const WW_IA_AGENT_REQUESTS_COLLECTION = 'ww-ai-agent-requests';
export const WW_IA_AGENT_MESSAGES_COLLECTION = 'messages';

export type AgentRequestStatus = 'pending' | 'processing' | 'completed' | 'error';

export interface AgentRequest extends Dto {
  userId: string;
  status: AgentRequestStatus;
  organization?: IDName;
  division?: IDName;
}

export type AgentResponseType = 'answer' | 'summary' | 'search_results' | 'clarification' | 'refusal' | 'error';

export type AgentResponseSourceType = 'document' | 'inspection' | 'issue' | 'asset' | 'warehouse' | 'tool' | 'other';

export interface AgentCitation {
  sourceId: string;
  locator?: string;
  pageNumber?: number;
  chunkIndex?: number;
  quote?: string;
}

export type AgentResponseTimestamp = Timestamp | Date | string | number;

export interface AgentResponseSource {
  id: string;
  type: AgentResponseSourceType;
  displayName?: string;
  title?: string;
  filename?: string;
  url?: string;
  storagePath?: string;
  pageNumber?: number;
  inspectionId?: string;
  inspectionName?: string;
  createdDate?: AgentResponseTimestamp;
  metadata?: Record<string, unknown>;
}

export interface AgentTextBlock {
  type: 'text';
  markdown: string;
  citationIds?: string[];
  basis?: AgentResponseBasis;
}

export interface AgentHeadingBlock {
  type: 'heading';
  level: 2 | 3 | 4;
  text: string;
}

export interface AgentListBlock {
  type: 'list';
  style: 'bullet' | 'numbered';
  items: Array<{ text: string; citationIds?: string[] }>;
  basis?: AgentResponseBasis;
}

export type AgentTableCellValue = string | number | boolean | null;

export interface AgentTableBlock {
  type: 'table';
  caption?: string;
  columns: Array<{ key: string; label: string; align?: 'left' | 'right' | 'center' }>;
  rows: Array<Partial<Record<string, AgentTableCellValue>>>;
  citationIds?: string[];
  basis?: AgentResponseBasis;
}

export type AgentMetricTone = 'neutral' | 'good' | 'warning' | 'danger';
export type AgentCalloutTone = 'info' | 'success' | 'warning' | 'danger';

export interface AgentMetricBlock {
  type: 'metric';
  label: string;
  value: string | number;
  unit?: string;
  tone?: AgentMetricTone;
  description?: string;
  citationIds?: string[];
  basis?: AgentResponseBasis;
}

export type AgentResponseBasis = 'affirmed' | 'inferred';

export interface AgentCalloutAdvisory {
  document: string;
  page?: number;
  signer?: string;
  date?: string;
  changeCondition: string;
  status?: 'complete' | 'degraded';
}

export interface AgentCalloutNotCovered {
  examinedDocuments: string[];
}

export interface AgentCalloutBlock {
  type: 'callout';
  tone: AgentCalloutTone;
  title?: string;
  markdown: string;
  citationIds?: string[];
  advisory?: AgentCalloutAdvisory;
  notCovered?: AgentCalloutNotCovered;
}

export interface AgentCitationsBlock {
  type: 'citations';
  citations: AgentCitation[];
}

export type AgentResponseBlock = AgentTextBlock | AgentHeadingBlock | AgentListBlock | AgentTableBlock | AgentMetricBlock | AgentCalloutBlock | AgentCitationsBlock;

export const AGENT_RESPONSE_SCHEMA_V1 = 'watson.agent.response.v1';
export const AGENT_RESPONSE_SCHEMA_V2 = 'watson.agent.response.v2';

export type AgentResponseSchemaVersion = typeof AGENT_RESPONSE_SCHEMA_V1 | typeof AGENT_RESPONSE_SCHEMA_V2;

export interface AgentResponse {
  schemaVersion: AgentResponseSchemaVersion;
  responseType: AgentResponseType;
  title?: string;
  summary?: string;
  blocks: AgentResponseBlock[];
  sources?: AgentResponseSource[];
  followUpQuestions?: string[];
}

export type AgentUserMessage = Dto & {
  role: 'user';
  content: string;
};

export type AgentAssistantMessage = Dto & {
  role: 'assistant';
  response: AgentResponse;
};

export type AgentMessage = AgentUserMessage | AgentAssistantMessage;
